import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import type { CalendarEntry, MealSlot } from '../../api/calendarTypes.js';
import { MEAL_ICON, MEAL_LABEL } from '../../api/calendarTypes.js';
import { listIngredients } from '../../api/ingredients.js';
import type { Ingredient } from '../../api/types.js';
import { unitToGramsFactor } from '../recipes/nutrition.js';
import {
  clearEntryIngredients,
  getEntryIngredients,
  setEntryIngredients,
  updateCalendarEntryPortions,
  type EntryIngredient,
} from '../../api/calendarEntries.js';

export interface EditEntryDialogProps {
  open: boolean;
  entry: CalendarEntry | null;
  onClose: () => void;
  onSaved: (entry: CalendarEntry) => void;
  onRemoved: (entryId: number) => void;
}

interface Row {
  key: string;
  ingredient_id: number;
  name: string;
  quantity: number;
  unit: string;
  isOverride: boolean;
  userEdited: boolean;
}

function newKey(): string {
  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatWeight(grams: number): string {
  if (grams >= 1000) return `${(grams / 1000).toFixed(2)} kg`;
  return `${Math.round(grams)} g`;
}

export function EditEntryDialog({
  open,
  entry,
  onClose,
  onSaved,
  onRemoved,
}: EditEntryDialogProps): ReactElement | null {
  const [portions, setPortions] = useState<number>(1);
  const [originalPortions, setOriginalPortions] = useState<number>(1);
  const [appliedPortions, setAppliedPortions] = useState<number>(1);
  const [rows, setRows] = useState<Row[]>([]);
  const [originalRows, setOriginalRows] = useState<Row[]>([]);
  const baselineRowsRef = useRef<Row[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loadingIngredients, setLoadingIngredients] = useState<boolean>(false);
  const [loadingOverrides, setLoadingOverrides] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pickingKey, setPickingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !entry) return;
    setPortions(entry.portions);
    setOriginalPortions(entry.portions);
    setAppliedPortions(entry.portions);
    setError(null);
    setLoadingIngredients(true);
    setLoadingOverrides(true);
    let cancelled = false;
    void (async () => {
      try {
        const list = await listIngredients();
        if (!cancelled) setIngredients(list);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load ingredients';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoadingIngredients(false);
      }
    })();
    void (async () => {
      try {
        const resp = await getEntryIngredients(entry.id);
        if (cancelled) return;
        const loaded: Row[] = resp.ingredients.map((i: EntryIngredient) => ({
          key: `db-${i.ingredient_id}`,
          ingredient_id: i.ingredient_id,
          name: i.name ?? `#${i.ingredient_id}`,
          quantity: i.quantity,
          unit: i.unit,
          isOverride: i.is_override ?? false,
          userEdited: false,
        }));
        setRows(loaded);
        setOriginalRows(loaded);
        baselineRowsRef.current = loaded;
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load overrides';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoadingOverrides(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, entry]);

  const appliedPortionsRef = useRef<number>(appliedPortions);
  appliedPortionsRef.current = appliedPortions;

  function rescaleFromPortions(newPortions: number): void {
    const baseline = appliedPortionsRef.current;
    if (!baseline || baseline <= 0) return;
    const factor = newPortions / baseline;
    if (factor === 1) return;
    setRows((cur) =>
      cur.map((r) => {
        if (r.isOverride || r.userEdited) return r;
        return { ...r, quantity: r.quantity * factor };
      }),
    );
    setAppliedPortions(newPortions);
  }

  function handlePortionsChange(next: number): void {
    setPortions(next);
    const safe = next > 0 ? next : 0;
    if (safe > 0 && safe !== appliedPortionsRef.current) {
      rescaleFromPortions(safe);
    }
  }

  function handlePortionsBlur(): void {
    const safe = portions > 0 ? portions : 0;
    if (safe > 0 && safe !== appliedPortionsRef.current) {
      rescaleFromPortions(safe);
    }
  }

  useEffect(() => {
    if (ingredients.length === 0) return;
    setRows((cur) => {
      let changed = false;
      const next = cur.map((r) => {
        if (r.ingredient_id <= 0) return r;
        if (!r.name.startsWith('#')) return r;
        const ing = ingredients.find((i) => i.id === r.ingredient_id);
        if (!ing) return r;
        changed = true;
        return { ...r, name: ing.name };
      });
      return changed ? next : cur;
    });
  }, [ingredients]);

  if (!entry) return null;

  const slot: MealSlot = entry.meal_type;
  const title = `${MEAL_ICON[slot]} ${MEAL_LABEL[slot]} — ${entry.recipe.title}`;
  const portionsDirty = portions !== originalPortions;
  const userRows = rows.filter((r) => r.userEdited);
  const originalUserRows = originalRows.filter((r) => r.userEdited);
  const rowsDirty =
    userRows.length !== originalUserRows.length ||
    userRows.some((r) => {
      const o = originalUserRows.find((x) => x.key === r.key);
      return (
        !o ||
        r.ingredient_id !== o.ingredient_id ||
        r.quantity !== o.quantity ||
        r.unit !== o.unit
      );
    });
  const dirty = portionsDirty || rowsDirty;

  const previewTotals = ((): { kcal: number; protein_g: number; carbs_g: number; fat_g: number; weight_g: number } => {
    const safePortions = portions > 0 ? portions : 1;
    const safeOriginalPortions = originalPortions > 0 ? originalPortions : 1;
    const scale = safePortions / safeOriginalPortions;
    const baseline = baselineRowsRef.current;
    let kcal = 0;
    let protein_g = 0;
    let carbs_g = 0;
    let fat_g = 0;
    let weight_g = 0;
    for (const r of rows) {
      if (r.ingredient_id <= 0) continue;
      const ing = ingredients.find((i) => i.id === r.ingredient_id);
      if (!ing) continue;
      const factor = unitToGramsFactor(r.unit, ing);
      const baselineRow = baseline.find((b) => b.key === r.key);
      const useBaselineScaling = baselineRow !== undefined && !r.isOverride && !r.userEdited;
      const grams = useBaselineScaling
        ? (baselineRow!.quantity * factor) * scale
        : r.quantity * factor;
      const k = grams / 100;
      kcal += k * ing.kcal_per_100;
      protein_g += k * ing.protein_per_100;
      carbs_g += k * ing.carbs_per_100;
      fat_g += k * ing.fat_per_100;
      weight_g += grams;
    }
    return { kcal, protein_g, carbs_g, fat_g, weight_g };
  })();

  function updateRow(key: string, patch: Partial<Row>, markAsOverride = false): void {
    setRows((cur) =>
      cur.map((r) =>
        r.key === key
          ? {
              ...r,
              ...patch,
              isOverride: markAsOverride ? true : r.isOverride,
              userEdited: true,
            }
          : r,
      ),
    );
  }

  function removeRow(key: string): void {
    setRows((cur) => cur.filter((r) => r.key !== key));
  }

  function addRow(): void {
    const k = newKey();
    setRows((cur) => [
      ...cur,
      { key: k, ingredient_id: 0, name: 'Pick…', quantity: 1, unit: 'g', isOverride: true, userEdited: true },
    ]);
    setPickingKey(k);
  }

  function pickIngredient(key: string, ingredientId: number): void {
    const ing = ingredients.find((i) => i.id === ingredientId);
    if (!ing) return;
    updateRow(key, { ingredient_id: ing.id, name: ing.name, unit: ing.default_unit });
    setPickingKey(null);
  }

  async function handleSave(): Promise<void> {
    if (!entry) return;
    setSaving(true);
    setError(null);
    try {
      let updated: CalendarEntry = entry;
      if (portionsDirty) {
        updated = await updateCalendarEntryPortions(entry.id, portions);
      }
      if (rowsDirty) {
        const payload: EntryIngredient[] = rows
          .filter((r) => r.ingredient_id > 0 && r.quantity > 0)
          .map((r) => ({ ingredient_id: r.ingredient_id, quantity: r.quantity, unit: r.unit }));
        await setEntryIngredients(entry.id, payload);
      }
      onSaved(updated);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleResetToRecipe(): Promise<void> {
    if (!entry) return;
    setSaving(true);
    setError(null);
    try {
      await clearEntryIngredients(entry.id);
      setRows([]);
      setOriginalRows([]);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to reset';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(): Promise<void> {
    if (!entry) return;
    setSaving(true);
    setError(null);
    try {
      const { deleteCalendarEntry } = await import('../../api/calendarEntries.js');
      await deleteCalendarEntry(entry.id);
      onRemoved(entry.id);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-3" data-testid="edit-entry-dialog">
        <div
          className="flex items-center justify-between rounded-2xl border p-3 text-sm"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
        >
          <div className="flex flex-col">
            <span className="font-medium" style={{ color: 'var(--text-main)' }}>
              Portions to cook
            </span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Recipe default: 1 portion = {entry.recipe.kcal_per_portion} kcal
            </span>
          </div>
          <input
            type="number"
            min={0.25}
            max={20}
            step={0.25}
            value={portions}
            onChange={(e) => handlePortionsChange(Number.parseFloat(e.target.value) || 0)}
            onBlur={handlePortionsBlur}
            className="h-9 w-20 rounded border px-2 text-right"
            style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
            data-testid="edit-entry-portions"
          />
        </div>

        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border p-3 text-sm"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
          data-testid="edit-entry-totals"
        >
          <span className="font-medium" style={{ color: 'var(--text-main)' }}>
            Total
          </span>
          <span style={{ color: 'var(--text-muted)' }}>
            <span data-testid="edit-entry-total-kcal" style={{ color: 'var(--text-main)', fontWeight: 600 }}>
              {Math.round(previewTotals.kcal)}
            </span>{' '}
            kcal
          </span>
          <span aria-hidden="true" style={{ color: 'var(--border)' }}>·</span>
          <span style={{ color: 'var(--text-muted)' }}>
            P{' '}
            <span data-testid="edit-entry-total-protein" style={{ color: 'var(--text-main)' }}>
              {previewTotals.protein_g.toFixed(1)}
            </span>
            g
          </span>
          <span aria-hidden="true" style={{ color: 'var(--border)' }}>·</span>
          <span style={{ color: 'var(--text-muted)' }}>
            C{' '}
            <span data-testid="edit-entry-total-carbs" style={{ color: 'var(--text-main)' }}>
              {previewTotals.carbs_g.toFixed(1)}
            </span>
            g
          </span>
          <span aria-hidden="true" style={{ color: 'var(--border)' }}>·</span>
          <span style={{ color: 'var(--text-muted)' }}>
            F{' '}
            <span data-testid="edit-entry-total-fat" style={{ color: 'var(--text-main)' }}>
              {previewTotals.fat_g.toFixed(1)}
            </span>
            g
          </span>
          <span aria-hidden="true" style={{ color: 'var(--border)' }}>·</span>
          <span style={{ color: 'var(--text-muted)' }}>
            <span data-testid="edit-entry-total-weight" style={{ color: 'var(--text-main)', fontWeight: 600 }}>
              {formatWeight(previewTotals.weight_g)}
            </span>
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-main)' }}>
            Ingredients ({rows.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={saving || loadingIngredients}
            className="flex min-h-[36px] items-center rounded-md border px-2 text-xs font-medium"
            style={{
              borderColor: 'var(--accent)',
              color: 'var(--accent)',
              background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
            }}
            data-testid="edit-entry-add"
          >
            + Add ingredient
          </button>
        </div>

        {loadingOverrides ? (
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Loading overrides…
          </div>
        ) : null}

        {rows.length === 0 && !loadingOverrides ? (
          <div
            className="rounded-md border border-dashed p-3 text-xs"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            data-testid="edit-entry-empty"
          >
            No override set. Shopping list will use the recipe’s default ingredients scaled by portions.
          </div>
        ) : null}

        <ul className="flex flex-col gap-2" data-testid="edit-entry-rows">
          {rows.map((r) => (
            <li
              key={r.key}
              className="flex items-center gap-2 rounded-md border p-2"
              style={{ borderColor: 'var(--border)' }}
              data-testid="edit-entry-row"
            >
              {pickingKey === r.key ? (
                <select
                  className="h-9 flex-1 rounded border px-2 text-sm"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
                  defaultValue=""
                  onChange={(e) => {
                    const v = Number.parseInt(e.target.value, 10);
                    if (Number.isFinite(v) && v > 0) pickIngredient(r.key, v);
                  }}
                  data-testid={`edit-entry-pick-${r.key}`}
                  autoFocus
                >
                  <option value="" disabled>
                    Pick ingredient…
                  </option>
                  {ingredients.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickingKey(r.key)}
                  className="h-9 flex-1 truncate rounded border px-2 text-left text-sm"
                  style={{
                    borderColor: 'var(--border)',
                    color: 'var(--text-main)',
                    background: 'transparent',
                  }}
                  data-testid={`edit-entry-name-${r.key}`}
                >
                  {r.name}
                </button>
              )}
              <input
                type="number"
                min={0}
                step={0.01}
                value={r.quantity}
                onChange={(e) => updateRow(r.key, { quantity: Number.parseFloat(e.target.value) || 0 }, true)}
                className="h-9 w-20 rounded border px-2 text-right text-sm"
                style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
                data-testid={`edit-entry-qty-${r.key}`}
              />
              <input
                type="text"
                value={r.unit}
                onChange={(e) => updateRow(r.key, { unit: e.target.value }, true)}
                className="h-9 w-16 rounded border px-2 text-center text-sm"
                style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
                data-testid={`edit-entry-unit-${r.key}`}
              />
              <span
                className="flex h-9 w-9 items-center justify-center rounded text-xs"
                style={{
                  background: r.isOverride
                    ? 'color-mix(in srgb, var(--accent) 20%, transparent)'
                    : 'transparent',
                  color: 'var(--text-muted)',
                  border: '1px solid var(--border)',
                }}
                title={r.isOverride ? 'Custom quantity (portions change will NOT rescale this)' : 'From recipe (portions change WILL rescale this)'}
                data-testid={`edit-entry-badge-${r.key}`}
              >
                {r.isOverride ? '✎' : '↻'}
              </span>
              <button
                type="button"
                onClick={() => removeRow(r.key)}
                aria-label={`Remove ${r.name}`}
                disabled={saving}
                className="flex h-9 w-9 items-center justify-center rounded border text-sm"
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--text-muted)',
                  background: 'transparent',
                }}
                data-testid={`edit-entry-remove-${r.key}`}
              >
                🗑
              </button>
            </li>
          ))}
        </ul>

        {error ? (
          <div
            className="rounded-md border p-2 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'color-mix(in srgb, #dc2626 8%, transparent)',
            }}
            role="alert"
            data-testid="edit-entry-error"
          >
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void handleResetToRecipe()}
            disabled={saving || rows.length === 0}
            className="flex min-h-[40px] items-center rounded-md border px-3 text-xs"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'transparent',
            }}
            data-testid="edit-entry-reset"
          >
            ↺ Reset to recipe
          </button>
          <button
            type="button"
            onClick={() => void handleRemove()}
            disabled={saving}
            className="flex min-h-[40px] items-center rounded-md border px-3 text-xs"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'transparent',
            }}
            data-testid="edit-entry-delete"
          >
            🗑 Remove entry
          </button>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex min-h-[40px] items-center rounded-md border px-3 text-sm"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
                background: 'transparent',
              }}
              data-testid="edit-entry-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !dirty}
              className="flex min-h-[40px] items-center rounded-md border px-3 text-sm font-medium"
              style={{
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
                background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
              }}
              data-testid="edit-entry-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
